// src/pages/admin/AdminDashboard.jsx
//
// Founder-ops glance — the default landing page when you open admin.
// Replaces the matching-era verification KPIs with four tiles that
// answer "is the platform OK today and is the founding-club pipeline
// moving?" plus a recent-admin-actions feed pulled from adminAudit/.
//
// See features/admin-dashboard-refactor/01-spec.md for the approved
// scope, data sources, and acceptance criteria.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  collectionGroup,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { usePlatformClaims } from '@/hooks/usePlatformClaims';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  TrophyIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  InboxStackIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

// Keep these in sync with src/pages/admin/AdminCostDashboard.jsx. The
// constants are duplicated rather than imported to avoid a
// page-to-page dependency for two numbers.
const COST_PER_SMS_USD    = 0.013;
const COST_PER_AI_MSG_USD = 0.006;

const FOUNDING_CLUB_MAX = 5;

const HEAD_FONT = "'Barlow Condensed', sans-serif";

function getCurrentMonthKey() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function formatUsd(n) {
  return `$${(Number(n) || 0).toFixed(2)}`;
}

function formatAuditTimestamp(ts) {
  if (!ts) return '';
  const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month:  '2-digit',
    day:    '2-digit',
    hour:   '2-digit',
    minute: '2-digit',
  });
}

function summarizeAudit(a) {
  if (a.action !== 'setClubTier') return a.action || 'admin action';
  const before = a.before || {};
  const after  = a.after  || {};
  const fields = ['tier', 'foundingClubNumber', 'trialEndsAt', 'seatLimit'];
  const parts = [];
  for (const f of fields) {
    const b = renderForFeed(before[f]);
    const af = renderForFeed(after[f]);
    if (b !== af) parts.push(`${labelOf(f)} ${b ?? '—'}→${af ?? '—'}`);
  }
  if (parts.length === 0) return 'no-op tier write';
  return parts.join(' · ');
}

function labelOf(f) {
  switch (f) {
    case 'tier':               return 'tier';
    case 'foundingClubNumber': return 'FC#';
    case 'trialEndsAt':        return 'trial';
    case 'seatLimit':          return 'seats';
    default: return f;
  }
}

function renderForFeed(v) {
  if (v == null) return null;
  if (typeof v === 'object' && typeof v.toDate === 'function') {
    return v.toDate().toISOString().slice(0, 10);
  }
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 10);
  return String(v);
}

export default function AdminDashboard() {
  const { isFounder } = usePlatformClaims();
  const blocked = !isFounder;

  const [loading, setLoading] = useState(true);
  const [errors, setErrors]   = useState({}); // { tile: message }

  // Tile state — null = loading, number = loaded
  const [foundingCount,   setFoundingCount]   = useState(null);
  const [smsThisMonth,    setSmsThisMonth]    = useState(null);
  const [aiThisMonth,     setAiThisMonth]     = useState(null);
  const [openFeedback,    setOpenFeedback]    = useState(null);
  const [recentAudits,    setRecentAudits]    = useState([]);

  async function loadAll() {
    setLoading(true);
    setErrors({});

    const monthKey = getCurrentMonthKey();

    // Run all 5 fetches in parallel; capture per-tile errors so one
    // failure doesn't blank the dashboard.
    const tasks = [
      ['founding', async () => {
        const q = query(
          collection(db, 'clubs'),
          where('foundingClubNumber', '>=', 1),
        );
        const snap = await getDocs(q);
        if (snap.size > FOUNDING_CLUB_MAX) {
          console.warn(
            `[AdminDashboard] foundingClubNumber count (${snap.size}) exceeds program max ${FOUNDING_CLUB_MAX}; ` +
            `data integrity issue — review /admin/clubs.`,
          );
        }
        setFoundingCount(snap.size);
      }],
      ['sms', async () => {
        // orderBy('count', 'desc') reuses the existing CG composite
        // index on (monthKey, count) — same index AdminCostDashboard
        // uses. Order is irrelevant for the sum operation; required to
        // satisfy Firestore's index check.
        const q = query(
          collectionGroup(db, 'smsUsage'),
          where('monthKey', '==', monthKey),
          orderBy('count', 'desc'),
        );
        const snap = await getDocs(q);
        const total = snap.docs.reduce((acc, d) => acc + Number(d.data()?.count || 0), 0);
        setSmsThisMonth(total);
      }],
      ['ai', async () => {
        const q = query(
          collectionGroup(db, 'aiUsage'),
          where('monthKey', '==', monthKey),
          orderBy('count', 'desc'),
        );
        const snap = await getDocs(q);
        const total = snap.docs.reduce((acc, d) => acc + Number(d.data()?.count || 0), 0);
        setAiThisMonth(total);
      }],
      ['feedback', async () => {
        const q = query(
          collection(db, 'feedback'),
          where('resolved', '==', false),
        );
        const snap = await getDocs(q);
        setOpenFeedback(snap.size);
      }],
      ['audit', async () => {
        const q = query(
          collection(db, 'adminAudit'),
          orderBy('createdAt', 'desc'),
          limit(5),
        );
        const snap = await getDocs(q);
        setRecentAudits(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }],
    ];

    const results = await Promise.all(
      tasks.map(async ([key, fn]) => {
        try { await fn(); return [key, null]; }
        catch (e) { return [key, e?.message || 'load failed']; }
      }),
    );
    const errMap = {};
    for (const [key, err] of results) if (err) errMap[key] = err;
    setErrors(errMap);
    setLoading(false);
  }

  useEffect(() => {
    if (blocked) return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocked]);

  if (blocked) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 mt-0.5" />
          <p>
            <span className="font-semibold">Insufficient privilege.</span>{' '}
            Founder claim required to view the admin dashboard.
          </p>
        </div>
      </div>
    );
  }

  const smsCost = (smsThisMonth ?? 0) * COST_PER_SMS_USD;
  const aiCost  = (aiThisMonth  ?? 0) * COST_PER_AI_MSG_USD;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="flex items-center justify-between mb-5">
        <h1
          className="text-3xl font-bold uppercase tracking-tight text-zinc-900"
          style={{ fontFamily: HEAD_FONT }}
        >
          Founder ops
        </h1>
        <button
          type="button"
          onClick={loadAll}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-100 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Tile row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Tile
          icon={<TrophyIcon className="h-5 w-5" />}
          label="Founding Clubs"
          value={
            foundingCount == null
              ? null
              : `${Math.min(foundingCount, FOUNDING_CLUB_MAX)} / ${FOUNDING_CLUB_MAX}`
          }
          caption="program slots filled"
          error={errors.founding}
        />
        <Tile
          icon={<ChatBubbleLeftRightIcon className="h-5 w-5" />}
          label="SMS this month"
          value={smsThisMonth == null ? null : smsThisMonth.toLocaleString()}
          caption={smsThisMonth == null ? '' : `est. ${formatUsd(smsCost)} Twilio`}
          error={errors.sms}
        />
        <Tile
          icon={<SparklesIcon className="h-5 w-5" />}
          label="AI calls this month"
          value={aiThisMonth == null ? null : aiThisMonth.toLocaleString()}
          caption={aiThisMonth == null ? '' : `est. ${formatUsd(aiCost)} Anthropic`}
          error={errors.ai}
        />
        <Tile
          icon={<InboxStackIcon className="h-5 w-5" />}
          label="Open feedback"
          value={openFeedback == null ? null : openFeedback}
          caption="unresolved beta items"
          error={errors.feedback}
        />
      </div>

      {/* Two-column row: recent admin actions + quick links */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6">
        {/* Recent admin actions — spans 2 cols */}
        <div className="lg:col-span-2 rounded-2xl border border-zinc-200 bg-white px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
              Recent admin actions
            </h2>
            <Link
              to="/admin/clubs"
              className="text-xs font-semibold text-lime-700 hover:underline"
            >
              View clubs →
            </Link>
          </div>

          {errors.audit && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 flex items-start gap-2 mb-3">
              <ExclamationTriangleIcon className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
              <span>{errors.audit}</span>
            </div>
          )}

          {!errors.audit && loading && (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-10 rounded-md bg-zinc-100 animate-pulse" />
              ))}
            </div>
          )}

          {!errors.audit && !loading && recentAudits.length === 0 && (
            <p className="text-sm text-zinc-500">No admin actions yet.</p>
          )}

          {!errors.audit && !loading && recentAudits.length > 0 && (
            <ul className="divide-y divide-zinc-100">
              {recentAudits.map((a) => {
                const summary = summarizeAudit(a);
                const isClub = a.targetType === 'club' && a.targetId;
                const inner = (
                  <div className="py-2.5">
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <CheckCircleIcon className="h-3.5 w-3.5 text-lime-500" />
                      <span>{formatAuditTimestamp(a.createdAt)}</span>
                      <span className="text-zinc-400">·</span>
                      <span className="font-mono truncate">{a.actorEmail || a.actorUid}</span>
                    </div>
                    <p className="mt-0.5 text-sm text-zinc-800">{summary}</p>
                    {a.reason && (
                      <p className="text-xs text-zinc-500">"{a.reason}"</p>
                    )}
                  </div>
                );
                return isClub ? (
                  <li key={a.id}>
                    <Link
                      to={`/admin/clubs/${a.targetId}`}
                      className="block hover:bg-zinc-50 -mx-2 px-2 rounded"
                    >
                      {inner}
                    </Link>
                  </li>
                ) : (
                  <li key={a.id}>{inner}</li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Quick links — 1 col */}
        <div className="rounded-2xl border border-zinc-200 bg-white px-5 py-4">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">
            Quick links
          </h2>
          <ul className="space-y-1.5 text-sm">
            <QuickLink to="/admin/clubs"          label="🏟️ Provision a club" />
            <QuickLink to="/admin/costs"          label="💰 Cost dashboard" />
            <QuickLink to="/admin/communications" label="💬 Communications" />
            <QuickLink to="/admin/social"         label="📅 Social autoposting" />
            <QuickLink to="/admin/ratings"        label="⭐ Ratings moderation" />
            <QuickLink to="/admin/feedback"       label="📨 Feedback inbox" />
            <QuickLink to="/admin/consent-audit"  label="📜 SMS Consent Log" />
            <QuickLink to="/admin/platform-claims" label="🔑 Platform Claims" />
          </ul>
        </div>
      </div>
    </div>
  );
}

function Tile({ icon, label, value, caption, error }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white px-4 py-3.5">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">
        <span className="text-lime-600">{icon}</span>
        <span>{label}</span>
      </div>
      {error ? (
        <p className="mt-2 text-xs text-red-700">Couldn't load: {error}</p>
      ) : value == null ? (
        <div className="mt-2 h-9 w-20 rounded-md bg-zinc-100 animate-pulse" />
      ) : (
        <>
          <div
            className="mt-1 text-3xl font-bold tabular-nums text-zinc-900"
            style={{ fontFamily: HEAD_FONT }}
          >
            {value}
          </div>
          {caption && <p className="text-xs text-zinc-500">{caption}</p>}
        </>
      )}
    </div>
  );
}

function QuickLink({ to, label }) {
  return (
    <li>
      <Link
        to={to}
        className="block px-2 py-1.5 rounded-md hover:bg-lime-50 text-zinc-800 hover:text-zinc-900"
      >
        {label}
      </Link>
    </li>
  );
}
